import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { FileText } from 'lucide-react';

import { api } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { formatCount, formatCurrency, formatDateTime } from '../lib/format';
import { EmptyState, ErrorNote, Panel, SearchBox, Spinner, TenderChips } from '../components/ui';
import { RangePicker } from '../components/RangePicker';
import { preset, rangeKey, rangeQuery, type RangeValue } from '../lib/range';
import { DocumentViewer, type DocumentRequest } from '../components/DocumentViewer';
import { useDebounced } from '../lib/useDebounced';

interface Purchase {
  purchaseId: number;
  at: string;
  total: string;
  netTotal: string;
  supplierName: string;
  tillName: string;
  userName: string;
  itemCount: number;
  methods: string[];
}

interface PurchasePage {
  items: Purchase[];
  nextCursor: string | null;
}

/**
 * Deliveries in, and the supplier invoice for each.
 *
 * The counterpart of the sales page. It exists chiefly because purchase invoices
 * had nowhere to be opened from: the till files 90 of them on dev01 and until
 * this page there was no route in the interface that reached one.
 *
 * ── Why there is no Returns tab here ──────────────────────────────────────
 *
 * Sales and refunds are two lists because a refund is a till event with its own
 * date and its own money. A *purchase* return is not: the till nets it against
 * the invoice it corrects, whenever it happened, because it changes what that
 * delivery cost. So a return shows as the gap between Invoiced and Net on the
 * invoice's own row, which is where it belongs, and a separate list would invite
 * somebody to add the two together.
 */
export function Purchases(): React.JSX.Element {
  const { activeStore } = useAuth();
  const storeId = activeStore?.storeId;
  const [range, setRange] = useState<RangeValue>(preset('last30'));
  const [search, setSearch] = useState('');
  const applied = useDebounced(search);
  const [viewing, setViewing] = useState<DocumentRequest | null>(null);

  const { data, error, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['purchases', storeId, rangeKey(range), applied],
      queryFn: ({ pageParam }) =>
        api.get<PurchasePage>(
          `/stores/${storeId}/purchases?${rangeQuery(range)}&limit=50` +
            `${applied ? `&search=${encodeURIComponent(applied)}` : ''}` +
            `${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`,
        ),
      initialPageParam: '',
      getNextPageParam: (last) => last.nextCursor ?? undefined,
      enabled: Boolean(storeId),
    });

  const purchases = data?.pages.flatMap((page) => page.items) ?? [];
  const timeZone = activeStore?.timezone ?? 'Europe/London';

  // The invoiced column only earns its place when something was sent back. A
  // column of blanks under a heading reads as data failing to load; a column
  // identical to the one beside it reads as a mistake.
  const anyReturned = purchases.some((purchase) => purchase.total !== purchase.netTotal);

  return (
    <>
      <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="qs-eyebrow">Transactions</p>
          <h1 className="text-lg font-medium tracking-tight">Purchases</h1>
        </div>
        <RangePicker value={range} onChange={setRange} />
      </header>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <SearchBox
          value={search}
          onChange={setSearch}
          label="Find a delivery by product"
          className="max-w-72 flex-1"
        />
      </div>

      {error && <ErrorNote error={error} />}

      <Panel>
        {isPending ? (
          <Spinner label="Loading deliveries…" />
        ) : purchases.length === 0 ? (
          <EmptyState>
            {applied
              ? `No delivery in this period contains a product matching “${applied}”.`
              : 'No deliveries in this period.'}
          </EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <caption className="sr-only">Deliveries received</caption>
              <thead>
                <tr className="border-b border-rule">
                  {/* The invoice number goes under the supplier's name on a
                      phone — see the row below. Five columns and a receipt
                      button do not fit 390px, and of the six this is the one
                      that identifies a delivery least: nobody is looking for
                      #877, they are looking for what Booker delivered on the
                      1st. */}
                  <th className="qs-eyebrow hidden py-2 pl-3 text-left sm:table-cell">Invoice</th>
                  <th className="qs-eyebrow py-2 pl-3 text-left sm:pl-0">When</th>
                  <th className="qs-eyebrow py-2 pr-3 text-left">Supplier</th>
                  <th className="qs-eyebrow hidden py-2 text-left lg:table-cell">Paid by</th>
                  <th className="qs-eyebrow hidden py-2 text-right md:table-cell">Lines</th>
                  {anyReturned && (
                    <th className="qs-eyebrow hidden py-2 text-right lg:table-cell">Invoiced</th>
                  )}
                  <th className="qs-eyebrow py-2 pr-3 text-right">
                    {anyReturned ? 'Net' : 'Total'}
                  </th>
                  <th className="py-2 pr-3">
                    <span className="sr-only">Invoice</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {purchases.map((purchase) => {
                  const returned = purchase.total !== purchase.netTotal;

                  return (
                    <tr key={purchase.purchaseId} className="border-b border-rule last:border-0">
                      <td className="qs-figure hidden py-1.5 pl-3 text-xs sm:table-cell">
                        #{purchase.purchaseId}
                      </td>
                      <td className="qs-figure py-1.5 pl-3 text-xs text-ink-muted sm:pl-0">
                        {formatDateTime(purchase.at, timeZone)}
                      </td>
                      {/* A supplier name is free text and can be one long word,
                          which would otherwise set this column's minimum width
                          and push the total off the screen. */}
                      <td className="py-1.5 pr-3 text-xs wrap-anywhere">
                        {purchase.supplierName}
                        {/* The invoice number, with its column dropped. */}
                        <span className="qs-figure block text-2xs text-ink-faint sm:hidden">
                          #{purchase.purchaseId}
                        </span>
                      </td>
                      <td className="hidden py-1.5 lg:table-cell">
                        <TenderChips methods={purchase.methods} />
                      </td>
                      <td className="qs-figure hidden py-1.5 text-right text-xs md:table-cell">
                        {formatCount(purchase.itemCount)}
                      </td>
                      {anyReturned && (
                        <td className="qs-figure hidden py-1.5 text-right text-xs text-ink-faint lg:table-cell">
                          {returned ? formatCurrency(purchase.total) : ''}
                        </td>
                      )}
                      <td className="qs-figure py-1.5 pr-3 text-right text-xs">
                        {formatCurrency(purchase.netTotal)}
                      </td>
                      <td className="py-1.5 pr-3 text-right">
                        <button
                          type="button"
                          title={`Invoice for delivery #${purchase.purchaseId}`}
                          onClick={() =>
                            setViewing({
                              path: `/stores/${storeId}/documents/purchase/${purchase.purchaseId}`,
                              title: `${purchase.supplierName} — invoice #${purchase.purchaseId}`,
                              // The till renders these as a PDF, never as till roll.
                              formats: ['pdf'],
                            })
                          }
                          className="rounded border border-rule px-1.5 py-0.5 text-ink-muted hover:border-accent hover:text-accent"
                        >
                          <FileText size={12} aria-hidden />
                          <span className="sr-only">
                            Invoice for delivery #{purchase.purchaseId}
                          </span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {hasNextPage && (
          <div className="border-t border-rule p-2 text-center">
            <button
              type="button"
              onClick={() => void fetchNextPage()}
              disabled={isFetchingNextPage}
              className="rounded border border-rule px-3 py-1.5 text-xs text-ink-muted hover:border-rule-strong hover:text-ink disabled:opacity-60"
            >
              {isFetchingNextPage ? 'Loading…' : `Load more (${purchases.length} shown)`}
            </button>
          </div>
        )}
      </Panel>

      <DocumentViewer request={viewing} onClose={() => setViewing(null)} />
    </>
  );
}
